import React, { useEffect, useState } from 'react';
import { IconProps, getIcon } from '../utils/iconUtils';

/**
 * A component that renders an SVG icon
 */
const Icon: React.FC<IconProps & { name: string }> = ({ 
  name, 
  size = 24, 
  color, 
  className, 
  style, 
  onClick,
  ...props 
}) => {
  const [IconComponent, setIconComponent] = useState<React.FC<IconProps> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadIconComponent = async () => {
      if (!isMounted) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const component = await getIcon(name);
        if (isMounted) {
          setIconComponent(component);
        }
      } catch (err) {
        console.error(`Failed to load icon: ${name}`, err);
        if (isMounted) {
          setError(`Failed to load icon: ${name}`);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadIconComponent();
    
    return () => {
      isMounted = false;
    };
  }, [name]);

  if (isLoading) {
    return (
      <div 
        style={{ 
          width: size, 
          height: size, 
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...style 
        }} 
      />
    );
  }

  if (error || !IconComponent) {
    return (
      <div 
        style={{ 
          width: size, 
          height: size, 
          display: 'inline-flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          color: '#666',
          fontSize: 12,
          borderRadius: '4px',
          ...style 
        }}
      >
        {name.charAt(0).toUpperCase()}
      </div>
    );
  }

  return (
    <IconComponent
      size={size}
      color={color}
      className={className}
      style={style}
      onClick={onClick}
      {...props}
    />
  );
};

export default Icon; 